import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  TreeDeciduous, 
  Sprout, 
  Trees,
  ArrowRight,
  CreditCard,
  User,
  CheckCircle2
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
  { id: 'sapling', price: 25, icon: Sprout },
  { id: 'young_tree', price: 50, icon: TreeDeciduous },
  { id: 'mature_tree', price: 100, icon: Trees },
];

export default function AdoptPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedTreeId = searchParams.get('tree');

  const [selectedPackage, setSelectedPackage] = useState('sapling');
  const [donorName, setDonorName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTree, setSelectedTree] = useState(null);

  useEffect(() => {
    if (selectedTreeId) {
      fetchTree(selectedTreeId);
    }
    if (user?.name) {
      setDonorName(user.name);
    }
  }, [selectedTreeId, user]);

  const fetchTree = async (treeId) => {
    try {
      const res = await fetch(`${API}/trees/${treeId}`);
      if (res.ok) {
        const tree = await res.json();
        setSelectedTree(tree);
      }
    } catch (e) {
      console.error('Failed to fetch tree');
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          package_id: selectedPackage,
          tree_id: selectedTreeId,
          origin_url: window.location.origin,
          donor_name: isAnonymous ? 'Anonymous' : (donorName || 'Anonymous')
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Checkout failed');
      }
    } catch (e) {
      console.error('Checkout error:', e);
      toast.error('Failed to create checkout session');
    } finally {
      setLoading(false);
    }
  };

  const getPackageLabel = (id) => {
    const labels = {
      sapling: t('adopt.sapling'),
      young_tree: t('adopt.youngTree'),
      mature_tree: t('adopt.matureTree')
    };
    return labels[id] || id;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="adopt-page">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="adopt-title">
              {t('adopt.title')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t('adopt.subtitle')}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Package Selection */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle>{t('adopt.selectPackage')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={selectedPackage}
                    onValueChange={setSelectedPackage}
                    className="grid gap-4"
                    data-testid="package-selector"
                  >
                    {PACKAGES.map((pkg) => {
                      const Icon = pkg.icon;
                      const isSelected = selectedPackage === pkg.id;
                      return (
                        <Label
                          key={pkg.id}
                          htmlFor={pkg.id}
                          className={`
                            flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all
                            ${isSelected 
                              ? 'border-primary bg-primary/5' 
                              : 'border-border hover:border-primary/50'
                            }
                          `}
                          data-testid={`package-${pkg.id}`}
                        >
                          <RadioGroupItem value={pkg.id} id={pkg.id} className="sr-only" />
                          <div className={`
                            w-14 h-14 rounded-xl flex items-center justify-center
                            ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'}
                          `}>
                            <Icon className="w-7 h-7" />
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-lg">{getPackageLabel(pkg.id)}</div>
                            <div className="text-sm text-muted-foreground">
                              {pkg.id === 'sapling' && 'Support a new seedling'}
                              {pkg.id === 'young_tree' && 'Adopt a growing tree'}
                              {pkg.id === 'mature_tree' && 'Sponsor a mature tree with care'}
                            </div>
                          </div>
                          <div className="text-2xl font-bold">${pkg.price}</div>
                          {isSelected && (
                            <CheckCircle2 className="w-6 h-6 text-primary" />
                          )}
                        </Label>
                      );
                    })}
                  </RadioGroup>

                  {/* Donor Name */}
                  <div className="mt-8 space-y-4">
                    <div className="flex items-center gap-2">
                      <User className="w-5 h-5 text-muted-foreground" />
                      <Label htmlFor="donor-name" className="text-base font-semibold">
                        {t('adopt.donorName')}
                      </Label>
                    </div>
                    
                    <Input
                      id="donor-name"
                      placeholder={t('adopt.donorNamePlaceholder')}
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      disabled={isAnonymous}
                      className="text-lg"
                      data-testid="donor-name-input"
                    />
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isAnonymous}
                        onChange={(e) => setIsAnonymous(e.target.checked)}
                        className="w-4 h-4 rounded border-border"
                        data-testid="anonymous-checkbox"
                      />
                      <span className="text-sm text-muted-foreground">{t('adopt.anonymous')}</span>
                    </label>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Summary Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Order Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Selected Package */}
                  <div className="flex items-center justify-between py-3 border-b border-border">
                    <span>{getPackageLabel(selectedPackage)}</span>
                    <span className="font-bold">
                      ${PACKAGES.find(p => p.id === selectedPackage)?.price}
                    </span>
                  </div>

                  {/* Selected Tree Info */}
                  {selectedTree && (
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm font-medium">Selected Tree</p>
                      <p className="text-xs text-muted-foreground mono mt-1">
                        {selectedTree.tree_id}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {selectedTree.species} • {selectedTree.lat.toFixed(4)}, {selectedTree.lng.toFixed(4)}
                      </p>
                    </div>
                  )}

                  {/* Donor Info */}
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm font-medium">Donor</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {isAnonymous ? 'Anonymous' : (donorName || 'Anonymous')}
                    </p>
                  </div>

                  {/* Total */}
                  <div className="flex items-center justify-between text-lg font-bold pt-4 border-t border-border">
                    <span>Total</span>
                    <span className="text-2xl">
                      ${PACKAGES.find(p => p.id === selectedPackage)?.price}
                    </span>
                  </div>

                  {/* Checkout Button */}
                  <Button
                    onClick={handleCheckout}
                    disabled={loading}
                    className="w-full btn-accent py-6 text-lg"
                    data-testid="checkout-btn"
                  >
                    {loading ? (
                      t('adopt.processing')
                    ) : (
                      <>
                        {t('adopt.proceedToPayment')}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    Secure payment powered by Stripe
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
}
